<script setup lang="ts">
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

export interface SancareListItemProps {
  href?: string | null
  isClickable?: boolean
  isSelected?: boolean
  to?: RouteLocationRaw | null
}

const props = withDefaults(defineProps<SancareListItemProps>(), {
  href: null,
  isClickable: false,
  isSelected: false,
  to: null,
})

const emit = defineEmits<{
  (e: 'click'): void
}>()

const isInteractive = computed(() => Boolean(props.to || props.href || props.isClickable))
const componentType = computed(() => {
  if (props.to) return 'router-link'
  if (props.href) return 'a'
  if (props.isClickable) return 'button'
  return 'div'
})

const handleClick = () => {
  if (props.isClickable) emit('click')
}
</script>

<template>
  <component
    :is="componentType"
    :to="to"
    :href="href"
    :type="componentType === 'button' ? 'button' : undefined"
    class="sancare-list-item gap-4 py-2 px-4"
    :class="{
      'sancare-list-item--interactive': isInteractive,
      'sancare-list-item--selectable': isSelected,
    }"
    @click="handleClick"
  >
    <div class="sancare-list-item__content-left gap-4">
      <div v-if="$slots['left']">
        <slot name="left" />
      </div>
      <slot />
    </div>
    <div v-if="$slots['right']">
      <slot name="right" />
    </div>
  </component>
</template>

<style lang="scss" scoped>
@import '@sancare/typescript-commons/src/assets/styles/variables';

.sancare-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border-bottom: 1px solid #beccdc;

  // No button or link styles
  color: inherit;
  font: inherit;
  text-decoration: none;
  text-align: inherit;
  background: transparent;
  border-left: 0;
  border-right: 0;
  border-top: 0;
  margin: 0;
  appearance: none;

  &--interactive {
    cursor: pointer;
  }

  &--selectable {
    cursor: pointer;
    background-color: #e4f0ff;
    color: $sancare-light-blue;
    font-weight: bold;
  }

  &__content-left {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}
</style>
